/* eslint-disable @typescript-eslint/naming-convention */
import hotkeys from 'hotkeys-js';
import { getLocaleStore } from '@/i18n';
import type { ServiceManager } from '../service-manager';
import { HotkeyProcessor } from './processor';
import { HotkeyType } from './type';

export class KeyboardHotkey extends HotkeyProcessor {
    private __escEventName = 'keyboard:esc';
    private __enterEventName = 'keyboard:enter';
    private __spaceEventName = 'keyboard:space';
    private __deleteEventName = 'keyboard:delete';
    private __selectAllEventName = 'keyboard:selectAll';
    private __altEventName = 'keyboard:alt';
    private __spaceDown = false;
    private __altDown = false;

    constructor(readonly service: ServiceManager) {
        super(service);
    }

    keyboardMount() {
        const { $t } = getLocaleStore();
        super.processorMount();
        super.register(
            ...[
                {
                    key: HotkeyType.ESC,
                    name: $t((v) => v.Text_ExitFullScreen),
                    win: 'esc',
                    mac: 'esc',
                    ignoreDisablePick: true,
                    showInKeyboardShortcut: false,
                },
                {
                    key: HotkeyType.DELETE,
                    name: $t((v) => v.Text_Delete),
                    win: 'Delete/Backspace',
                    mac: 'Delete',
                },
                {
                    key: HotkeyType.SELECT_ALL,
                    name: $t((v) => v.Text_SelectAll),
                    win: 'Ctrl A',
                    mac: '⌘ A',
                },
                {
                    key: HotkeyType.SPACE,
                    name: $t((v) => v.Text_HandTool),
                    win: 'Space',
                    mac: 'Space',
                },
            ],
        );

        hotkeys('Esc', this.__escHandle.bind(this));
        hotkeys('Enter', this.__enterHandle.bind(this));
        hotkeys('Space', { keyup: true }, this.__spaceHandle.bind(this));
        hotkeys(['Delete', 'Backspace'].join(','), this.__deleteHandle.bind(this));
        hotkeys(['Ctrl+A', 'Command+A'].join(','), this.__selectAllHandle.bind(this));
        // HACK: 无法单独绑定alt，使用*代替
        hotkeys('*', { keyup: true }, this.__altHandle.bind(this));
    }

    keyboardUnmount() {
        super.processorUnmount();
        hotkeys.unbind('*', this.__altHandle.bind(this));
        hotkeys.unbind('ESC', this.__escHandle.bind(this));
        hotkeys.unbind('Enter', this.__enterHandle.bind(this));
        hotkeys.unbind('Space', this.__spaceHandle.bind(this));
        hotkeys.unbind(['Delete', 'Backspace'].join(','), this.__deleteHandle.bind(this));
        hotkeys.unbind(['Ctrl+A', 'Command+A'].join(','), this.__selectAllHandle.bind(this));
    }

    onEsc(fn: () => void, weight = 0) {
        return this.service.events.on(this.__escEventName, fn, weight);
    }

    onEnter(fn: () => void, weight = 0) {
        return this.service.events.on(this.__enterEventName, fn, weight);
    }

    onDelete(fn: (args?: Record<string, any>) => void, weight = 0) {
        return this.service.events.on(this.__deleteEventName, fn, weight);
    }

    onSpace(fn: (args: { isKeydown: boolean; isKeyup: boolean }) => void, weight = 0) {
        return this.service.events.on(this.__spaceEventName, fn, weight);
    }

    onSelectAll(fn: () => void, weight = 0) {
        return this.service.events.on(this.__selectAllEventName, fn, weight);
    }

    onAlt(fn: (args: { isKeydown: boolean; isKeyup: boolean }) => void, weight = 0) {
        return this.service.events.on(this.__altEventName, fn, weight);
    }

    private __altHandle(e: Event) {
        if (hotkeys.alt || this.__altDown) {
            e.preventDefault();
            this.__emitAlt(e.type === 'keydown');
        }
    }

    __emitAlt(isKeydown: boolean) {
        if (this._checkDisablePressEvent()) return;
        if (isKeydown) {
            if (this.__altDown) return;
            this.__altDown = true;
            this.service.events.emit(this.__altEventName, { isKeydown: true, isKeyup: false });
        } else {
            this.__altDown = false;
            this.service.events.emit(this.__altEventName, { isKeydown: false, isKeyup: true });
        }
    }

    private __spaceHandle(e: Event) {
        this.__emitSpace(e.type === 'keydown');
    }

    __emitSpace(isKeydown: boolean) {
        if (this._checkDisablePressEvent(false, true)) return;
        if (isKeydown) {
            if (this.__spaceDown) return;
            this.__spaceDown = true;
            this.service.events.emit(this.__spaceEventName, { isKeydown: true, isKeyup: false });
        } else {
            this.__spaceDown = false;
            this.service.events.emit(this.__spaceEventName, { isKeydown: false, isKeyup: true });
        }
    }

    private __enterHandle() {
        this.__emitEnter();
    }

    __emitEnter() {
        if (this._checkDisablePressEvent()) return;
        this.service.events.emit(this.__enterEventName);
    }

    private __escHandle() {
        this.__emitEsc();
    }

    __emitEsc() {
        if (this._checkDisablePressEvent(true, true)) return;
        this.service.events.emit(this.__escEventName);
    }

    private __deleteHandle(e: Event) {
        // 阻止内嵌客户端将删除键作为后退操作，即使当前不允许删除图层。
        e.preventDefault();
        this.__emitDelete({ from: 'keyboard' });
    }

    __emitDelete(args?: { from: string }) {
        if (this._checkDisablePressEvent()) return;
        this.service.events.emit(this.__deleteEventName, args);
    }

    private __selectAllHandle(e?: Event) {
        e?.preventDefault();
        this.__emitSelectAll();
    }

    __emitSelectAll() {
        if (this._checkDisablePressEvent()) return;
        this.service.events.emit(this.__selectAllEventName);
    }
}
